import {
  GraphQLClient,
  GraphQLErrorMessage,
} from "../../../../../shared/gql-client";
import CrudMutateSliceModel, {
  CrudCreateModel,
  CrudDeleteModel,
  CrudUpdateModel,
} from "./crudMutateSliceModel";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { z } from "zod";
import { toast } from "sonner";
import i18next from "i18next";
import { GraphQLReadResponse } from "../../../domain/models/graphql";
import CrudService from "../../../crudGraphql/infrastructure/services/crudGraphqlService";
import { updatePackingSlipExtraReducers } from "../../../../planning/packingSlip/infrastructure/redux/PackingSlipThunks";

const crudAPI = new CrudService();

export const crudRead = createAsyncThunk(
  "crudGet",
  async ({ id }: { id: number }, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    const { crudMutate } = thunkAPI.getState() as {
      crudMutate: CrudMutateSliceModel;
    };
    crudAPI.init(client, crudMutate.schema);
    try {
      const response = (await crudAPI.get(
        id,
        crudMutate.prefix,
        crudMutate.queryExclude,
      )) as GraphQLReadResponse;
      return response.data.length > 0 ? response.data[0] : {};
    } catch (err) {
      console.error(err);
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const crudCreate = createAsyncThunk(
  "crudCreate",
  async ({ data, mutation }: CrudCreateModel, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    const { crudMutate } = thunkAPI.getState() as {
      crudMutate: CrudMutateSliceModel;
    };
    crudAPI.init(client, crudMutate.schema);
    try {
      return await crudAPI.mutate(
        data,
        mutation,
        crudMutate.queryExclude,
        crudMutate.mutationExclude,
      );
    } catch (err) {
      console.error(err);
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const crudUpdate = createAsyncThunk(
  "crudUpdate",
  async ({ data, mutation }: CrudUpdateModel, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    const { crudMutate } = thunkAPI.getState() as {
      crudMutate: CrudMutateSliceModel;
    };
    crudAPI.init(client, crudMutate.schema);
    try {
      return await crudAPI.mutate(
        data,
        mutation,
        crudMutate.queryExclude,
        crudMutate.mutationExclude,
      );
    } catch (err) {
      console.error(err);
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const crudDelete = createAsyncThunk(
  "crudDelete",
  async ({ id, mutation }: CrudDeleteModel, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };

    const data = {
      id: id,
    };

    const schema = z.object({
      id: z.number(),
    });

    crudAPI.init(client, schema);
    try {
      return await crudAPI.mutate(data, mutation);
    } catch (err) {
      console.error(err);
      return thunkAPI.rejectWithValue(err);
    }
  },
);

const initialState: CrudMutateSliceModel = {
  loading: true,
  model: {},
  schema: z.object({}),
  prefix: "",
  updated: false,
  queryExclude: [],
  mutationExclude: [],
};

const crudMutateSlice = createSlice({
  name: "crudMutate",
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    initMutate: (
      state,
      action: {
        payload: {
          schema: z.ZodObject<z.ZodRawShape>;
          prefix: string;
          queryExclude?: string[];
          mutationExclude?: string[];
        };
      },
    ) => {
      state.schema = action.payload.schema;
      state.prefix = action.payload.prefix;
      state.queryExclude = action.payload.queryExclude || [];
      state.mutationExclude = action.payload.mutationExclude || [];
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(crudRead.fulfilled, (state, action) => {
      state.model = action.payload;
      state.loading = false;
    });
    builder.addCase(crudCreate.fulfilled, (state, action) => {
      state.model = action.payload;
      toast.success(i18next?.t(`common:crud.${state.prefix}.created`));
    });
    builder.addCase(crudUpdate.fulfilled, (state, action) => {
      state.model = action.payload;
      toast.success(i18next?.t(`common:crud.${state.prefix}.updated`));
    });
    updatePackingSlipExtraReducers(builder);
    builder.addMatcher(
      (action) =>
        [crudCreate.fulfilled.type, crudUpdate.fulfilled.type].includes(
          action.type,
        ),
      (state) => {
        state.updated = true;
      },
    );
    builder.addMatcher(
      (action) =>
        [crudCreate.pending.type, crudUpdate.pending.type].includes(
          action.type,
        ),
      (state) => {
        state.updated = false;
      },
    );
    builder.addMatcher(
      (action) =>
        [
          crudCreate.rejected.type,
          crudUpdate.rejected.type,
          crudDelete.rejected.type,
        ].includes(action.type),
      (state, action: { payload: GraphQLErrorMessage }) => {
        state.loading = false;
        console.error(action.payload);
        toast.error(action.payload.message);
      },
    );
  },
});

export const { clear, setLoading, initMutate } = crudMutateSlice.actions;

export default crudMutateSlice.reducer;
